import axios, { AxiosError, isCancel } from 'axios';

import { REACT_APP_CE_API_BASEURL } from '../../../../constants/envConstants';
import { URL_CONSTANTS } from '../../../../constants/urlConstants';
import apiService from '../../../../services/api.service';
import { ICustomFieldDefinition } from '../CustomFields/interfaces/custom-fields.interface';
import { GlobalFilterSectionOption } from '../GlobalSearchBar/globalSearchBar.interface';
import {
  isFilterSectionOptionValue,
  parseFilterSectionOptionValue,
  resolveFilterSectionSearchOptions,
  setCachedFilterSectionSearchOptions,
} from '../GlobalSearchBar/helpers/global-filter-section.helper';

const parseJsonIfString = (value: unknown): unknown => {
  if (typeof value !== 'string') {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const normalizeCatalogOption = (
  item: unknown
): GlobalFilterSectionOption | null => {
  if (typeof item === 'string' && isFilterSectionOptionValue(item)) {
    const parsed = parseFilterSectionOptionValue(item);
    if (!parsed) {
      return null;
    }

    return {
      entity_type_id: parsed.entityTypeId,
      key_name: parsed.keyName,
      label: '',
    };
  }

  if (!item || typeof item !== 'object') {
    return null;
  }

  const row = item as {
    entity_type_id?: number | string;
    entityTypeId?: number | string;
    key_name?: string;
    keyName?: string;
    label?: string;
    display_label?: string;
    option_label?: string;
  };
  const entityTypeId = Number(row.entity_type_id ?? row.entityTypeId);
  const keyName = row.key_name || row.keyName;
  const labelCandidates = [row.label, row.display_label, row.option_label];
  const matchedLabel = labelCandidates.find(
    value => typeof value === 'string' && Boolean(value.trim())
  );
  const label = typeof matchedLabel === 'string' ? matchedLabel.trim() : '';

  if (!Number.isFinite(entityTypeId) || !keyName) {
    return null;
  }

  return {
    entity_type_id: entityTypeId,
    key_name: keyName,
    label,
  };
};

const extractRawOptions = (response: unknown): unknown[] => {
  const payload = parseJsonIfString(response);

  if (Array.isArray(payload)) {
    return payload;
  }

  if (!payload || typeof payload !== 'object') {
    return [];
  }

  const body = payload as {
    options?: unknown;
    data?: unknown;
  };
  const data = parseJsonIfString(body.data);
  const nestedData =
    data && typeof data === 'object' && !Array.isArray(data)
      ? (data as { options?: unknown; data?: unknown })
      : null;

  const candidates = [
    body.options,
    data,
    nestedData?.options,
    nestedData?.data,
  ];

  return (candidates.find(Array.isArray) as unknown[] | undefined) || [];
};

const parseOptions = (response: unknown): GlobalFilterSectionOption[] =>
  extractRawOptions(response)
    .map(normalizeCatalogOption)
    .filter((item): item is GlobalFilterSectionOption => item != null);

export const fetchGlobalFilterSectionOptions = async (): Promise<
  GlobalFilterSectionOption[]
> => {
  try {
    const response = await apiService.get(
      URL_CONSTANTS.COMPANY_CONFIG.globalFilterSectionOptions
    );
    const saved = parseOptions(response);
    const resolved = resolveFilterSectionSearchOptions(saved);
    if (resolved.length || !saved.length) {
      setCachedFilterSectionSearchOptions(resolved);
    }
    return saved;
  } catch (error: unknown) {
    console.error('Error fetching global filter section options', error);
    return [];
  }
};

export const saveGlobalFilterSectionOptions = async (
  options: GlobalFilterSectionOption[]
): Promise<{ error: boolean; message: string }> => {
  try {
    const response = await apiService.put(
      URL_CONSTANTS.COMPANY_CONFIG.globalFilterSectionOptions,
      { options }
    );

    if (response?.status || response?.success || response?.statusCode === 200) {
      setCachedFilterSectionSearchOptions(
        resolveFilterSectionSearchOptions(options)
      );
      return {
        error: false,
        message: response?.message || 'Form field options saved successfully.',
      };
    }

    return {
      error: true,
      message: response?.message || 'Failed to save form field options.',
    };
  } catch (error: unknown) {
    return {
      error: true,
      message:
        error instanceof AxiosError
          ? error?.response?.data?.message ||
            error?.response?.data?.error ||
            'Failed to save form field options.'
          : 'Failed to save form field options.',
    };
  }
};

const parseEntityItems = (
  response: { data?: unknown; items?: unknown[] } | null | undefined
): unknown[] => {
  const data = response?.data;
  if (Array.isArray(data)) {
    return data;
  }
  if (
    data &&
    typeof data === 'object' &&
    Array.isArray((data as { items?: unknown[] }).items)
  ) {
    return (data as { items: unknown[] }).items;
  }
  if (Array.isArray(response?.items)) {
    return response.items;
  }
  return [];
};

export const fetchFilterSectionEntities = async (): Promise<unknown[]> => {
  try {
    const response = await apiService.getWithDiffBaseUrl(
      URL_CONSTANTS.ENTITY_CONFIG.fetchAllEntityList,
      null,
      URL_CONSTANTS.ENTITY_CONFIG.baseUrl
    );
    return parseEntityItems(response);
  } catch (error: unknown) {
    console.error('Error fetching filter section entities', error);
    return [];
  }
};

export const fetchFilterSectionFields = async (
  entityTypeId: number
): Promise<ICustomFieldDefinition[]> => {
  try {
    const response = await axios.get(
      `${REACT_APP_CE_API_BASEURL}/api/v1/custom-fields/get-all-list/${entityTypeId}`
    );
    if (Array.isArray(response?.data?.data)) {
      return response.data.data as ICustomFieldDefinition[];
    }
    return [];
  } catch (error: unknown) {
    console.error('Error fetching filter section fields', error);
    return [];
  }
};

const parseReportRows = (response: unknown): Record<string, unknown>[] => {
  if (Array.isArray(response)) {
    return response as Record<string, unknown>[];
  }

  const payload = response as {
    data?: unknown;
    items?: unknown[];
  };

  if (Array.isArray(payload?.data)) {
    return payload.data as Record<string, unknown>[];
  }

  const nestedData = payload?.data as
    | { data?: unknown[]; items?: unknown[] }
    | undefined;

  if (Array.isArray(nestedData?.data)) {
    return nestedData.data as Record<string, unknown>[];
  }

  if (Array.isArray(nestedData?.items)) {
    return nestedData.items as Record<string, unknown>[];
  }

  if (Array.isArray(payload?.items)) {
    return payload.items as Record<string, unknown>[];
  }

  return [];
};

let reportDataController: AbortController | null = null;

export const searchFormModuleReportData = async ({
  entityTypeId,
  keyName,
  searchValue,
}: {
  entityTypeId: number;
  keyName: string;
  searchValue: string;
}): Promise<Record<string, unknown>[] | null> => {
  reportDataController?.abort();
  reportDataController = new AbortController();

  try {
    const response = await axios.get(
      `${URL_CONSTANTS.ENTITY.BASE_URL}${URL_CONSTANTS.ENTITY.ENDPOINTS.REPORT_DATA}`,
      {
        params: {
          table: entityTypeId,
          select: JSON.stringify(['id', keyName]),
          filters: JSON.stringify({
            field: keyName,
            operator: 'contains',
            value: searchValue,
          }),
          offset: 0,
          limit: 10,
        },
        signal: reportDataController.signal,
      }
    );

    return parseReportRows(response?.data).filter(row => row?.id != null);
  } catch (error: unknown) {
    if (
      isCancel(error) ||
      (error as { code?: string })?.code === 'ERR_CANCELED'
    ) {
      return null;
    }
    console.error('Error searching form module report data', error);
    return [];
  }
};
